/**
 * SmartImage Component
 * Reliable image loading with LQIP placeholder and error handling
 * 
 * Features:
 * - Renders LQIP (Low Quality Image Placeholder) immediately
 * - Loads full image in background
 * - Crossfade transition on load
 * - Clean fallback on error (no broken image icon)
 * - Supports different sizes: thumb, card, full
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ImageOff } from 'lucide-react';

// Default placeholder - base64 1x1 transparent pixel
const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

// Default fallback gradient colors by type
const FALLBACK_COLORS = {
  default: 'from-zinc-800 to-zinc-900',
  avatar: 'from-purple-900/30 to-zinc-900',
  cover: 'from-orange-900/20 to-zinc-900',
  artwork: 'from-pink-900/20 to-zinc-900',
};

/**
 * Resolve image URL to handle relative paths
 */
const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  return url.startsWith('/') ? url : `/${url}`;
};

/**
 * Generate LQIP URL from a Cloudinary URL if not provided
 */
const generateLqipUrl = (originalUrl) => {
  if (!originalUrl || !originalUrl.includes('cloudinary.com') || !originalUrl.includes('/upload/')) {
    return null;
  }
  
  // Insert LQIP transforms into Cloudinary URL
  const [base, path] = originalUrl.split('/upload/');
  if (!path) return null;
  
  // Remove existing transforms if present
  const cleanPath = path.replace(/^[^/]+\//, (match) => {
    // Check if it's a transform (contains underscore like w_100)
    return match.includes('_') ? '' : match;
  });
  
  return `${base}/upload/w_20,h_27,c_fill,g_auto,q_30,e_blur:100,f_auto/${path}`;
};

const SmartImage = ({
  // Image sources
  src,              // Primary image URL (full/card/thumb)
  lqipSrc,          // Optional LQIP URL (auto-generated if not provided)
  fallbackSrc,      // Optional fallback if main fails
  
  // Display options
  alt = '',
  className = '',
  style = {},
  objectFit = 'cover',
  aspectRatio,      // e.g., '2/3', '1/1', '16/9'
  
  // Behavior
  type = 'default', // 'default' | 'avatar' | 'cover' | 'artwork' - affects fallback style
  showIcon = false, // Show icon in fallback
  lazy = true,      // Use lazy loading
  priority = false, // Load immediately (disables lazy)
  
  // Events
  onLoad,
  onError,
  
  // For debugging
  'data-testid': testId,
}) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const [currentSrc, setCurrentSrc] = useState(null);
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  
  // Resolve URLs
  const resolvedSrc = resolveUrl(src);
  const resolvedLqip = resolveUrl(lqipSrc) || generateLqipUrl(resolvedSrc);
  const resolvedFallback = resolveUrl(fallbackSrc);
  
  // Handle image load
  const handleLoad = useCallback(() => {
    setStatus('loaded');
    onLoad?.();
  }, [onLoad]);
  
  // Handle image error
  const handleError = useCallback(() => {
    // Try fallback if available
    if (resolvedFallback && currentSrc !== resolvedFallback) {
      setCurrentSrc(resolvedFallback);
      return;
    }
    setStatus('error');
    onError?.();
  }, [resolvedFallback, currentSrc, onError]);
  
  // Setup intersection observer for lazy loading
  useEffect(() => {
    if (!lazy || priority || !resolvedSrc) {
      setCurrentSrc(resolvedSrc);
      return;
    }
    
    // Check if image is already in viewport
    const img = imgRef.current?.parentElement;
    if (!img) {
      setCurrentSrc(resolvedSrc);
      return;
    }
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrentSrc(resolvedSrc);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: '200px', // Start loading when 200px from viewport
        threshold: 0,
      }
    );
    
    observerRef.current.observe(img);
    
    return () => {
      observerRef.current?.disconnect();
    };
  }, [resolvedSrc, lazy, priority]);
  
  // Update src if prop changes
  useEffect(() => {
    if (!lazy || priority) {
      setCurrentSrc(resolvedSrc);
    }
  }, [resolvedSrc, lazy, priority]);
  
  // Container styles
  const containerStyles = {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#18181b', // zinc-900
    aspectRatio: aspectRatio,
    ...style,
  };
  
  // Image base styles
  const imageBaseStyles = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit,
  };
  
  // Get fallback gradient
  const fallbackGradient = FALLBACK_COLORS[type] || FALLBACK_COLORS.default;
  
  return (
    <div 
      className={`smart-image ${className}`} 
      style={containerStyles}
      data-testid={testId}
      data-status={status}
    >
      {/* LQIP Layer - always rendered first */}
      {resolvedLqip && status !== 'error' && (
        <img
          ref={imgRef}
          src={resolvedLqip}
          alt=""
          aria-hidden="true"
          className="lqip-layer"
          style={{
            ...imageBaseStyles,
            filter: 'blur(8px)',
            transform: 'scale(1.1)', // Prevent blur edge artifacts
            opacity: status === 'loaded' ? 0 : 1,
            transition: 'opacity 0.3s ease-out',
          }}
          loading="eager"
          decoding="sync"
        />
      )}
      
      {/* Main Image Layer */}
      {currentSrc && status !== 'error' && (
        <img
          src={currentSrc}
          alt={alt}
          className="main-image-layer"
          style={{
            ...imageBaseStyles,
            opacity: status === 'loaded' ? 1 : 0,
            transition: 'opacity 0.3s ease-in',
          }}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      
      {/* Error Fallback Layer */}
      {status === 'error' && (
        <div 
          className={`error-fallback bg-gradient-to-br ${fallbackGradient}`}
          style={{
            ...imageBaseStyles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {showIcon && (
            <ImageOff className="w-8 h-8 text-zinc-600" />
          )}
        </div>
      )}
      
      {/* No Image Fallback (when src is null/undefined) */}
      {!resolvedSrc && status !== 'error' && (
        <div 
          className={`no-image-fallback bg-gradient-to-br ${fallbackGradient}`}
          style={{
            ...imageBaseStyles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {showIcon && (
            <ImageOff className="w-6 h-6 text-zinc-600" />
          )}
        </div>
      )}
    </div>
  );
};

// Preset variants for common use cases
export const AvatarImage = (props) => (
  <SmartImage
    type="avatar"
    objectFit="cover"
    aspectRatio="1/1"
    className="rounded-full"
    {...props}
  />
);

export const CoverImage = (props) => (
  <SmartImage
    type="cover"
    objectFit="cover"
    aspectRatio="2/3"
    {...props}
  />
);

export const CardImage = (props) => (
  <SmartImage
    type="cover"
    objectFit="cover"
    aspectRatio="3/4"
    {...props}
  />
);

export const ArtworkImage = (props) => (
  <SmartImage
    type="artwork"
    objectFit="cover"
    {...props}
  />
);

export const HeroImage = (props) => (
  <SmartImage
    type="default"
    objectFit="cover"
    aspectRatio="21/9"
    priority
    {...props}
  />
);

export default SmartImage;
